import { useTranslation } from 'react-i18next'
import TruckIcon from '@mui/icons-material/LocalShippingOutlined'
import ManIcon from '@mui/icons-material/PersonOutline'
import DistanceIcon from '@mui/icons-material/MapOutlined'
import DurationIcon from '@mui/icons-material/TimerOutlined'
import ProductIcon from '@mui/icons-material/ShoppingCartOutlined'

import { convertKmsToMiles } from '../../../utils/conversion'
import { formatNumber } from '../../../utils/number'
import { useTimeFormatter } from '../../../utils/time'
import { useGetNbProductsInQuoteJob, type Quote } from '../../../modules/quotes'
import { useQuoteContext } from '../QuoteComponent.context'

const ONE_KILOMETER_METERS = 1000

type JobSummaryProps = {
  quote: Quote
  jobIndex: number
}

const JobSummary: React.FC<JobSummaryProps> = ({
  quote,
  jobIndex,
}) => {
  const { t } = useTranslation()
  const { formatSeconds } = useTimeFormatter()
  const { customerDistanceAndDuration, useMiles } = useQuoteContext()
  const getNbProductsInQuoteJob = useGetNbProductsInQuoteJob()

  const job = quote.jobs[jobIndex]
  if (!job) {
    return null
  }

  const getDistance = () => {
    const distanceMeters = customerDistanceAndDuration
      ? customerDistanceAndDuration[jobIndex]?.distanceMeters
      : job.jobResolvedBranch.totalDistance

    if (!distanceMeters) {
      return
    }
    if (useMiles) {
      return formatNumber(convertKmsToMiles(distanceMeters / ONE_KILOMETER_METERS))
    }
    return formatNumber(parseFloat((distanceMeters / ONE_KILOMETER_METERS).toFixed(1)))
  }

  const getDuration = () => {
    const durationSeconds = customerDistanceAndDuration
      ? customerDistanceAndDuration[jobIndex]?.durationSeconds
      : job.jobResolvedBranch.totalDuration

    if (!durationSeconds) {
      return
    }

    return formatSeconds(durationSeconds)
  }

  const distance = getDistance()
  const duration = getDuration()
  const nbProducts = getNbProductsInQuoteJob()(job)

  const nbTrucks = job.jobResolvedBranch.nbTrucks
  const nbMen = job.jobResolvedBranch.nbMen

  if (!nbTrucks?.length && !nbMen && !distance && !duration) {
    return null
  }

  return (
    <>
      <h2 className="mb-4 font-body2 text-sm font-bold text-neutral-700 md:mb-6">
        { t('quotes.quote.summary') }
      </h2>

      <div className="flex gap-4 text-base leading-tight text-gray-500 md:mb-8">

        { (!!nbTrucks?.length || !!nbMen) && (
          <div className="flex w-1/2 flex-col gap-2">
            { !!nbTrucks?.length && (
              <div>
                { nbTrucks.map(({ key, value }) => (
                  <div
                    key={key}
                    className="flex items-center gap-2"
                  >
                    <TruckIcon className="!text-[18px] text-neutral-400" />
                    <div>
                      <div className="mr-1 inline-block font-bold text-neutral-600">
                        { value } x
                      </div>
                      { t(`trucks.${key}.title`, { count: value }) }
                    </div>
                  </div>
                )) }
              </div>
            ) }

            { !!nbMen && (
              <div className="flex items-center gap-2">
                <ManIcon className="!text-[18px] text-neutral-400" />
                <div>
                  { t('quotes.quote.nbMen', { count: nbMen }) }
                </div>
              </div>
            ) }

            { nbProducts > 0 && (
              <div className="flex items-center gap-2">
                <ProductIcon className="!text-[18px] text-neutral-400" />
                <div>
                  { t('quotes.quote.nbProducts', { count: nbProducts }) }
                </div>
              </div>
            ) }
          </div>
        ) }

        <div className="flex w-1/2 flex-col gap-2">

          { distance && (
            <div className="flex items-center gap-2">
              <DistanceIcon className="!text-[18px] text-neutral-400" />
              <div>
                { distance }
                { ' ' }
                { t(useMiles ? 'units.mile' : 'units.km') }
              </div>
            </div>
          ) }

          { duration && (
            <div className="flex items-center gap-2">
              <DurationIcon className="!text-[18px] text-neutral-400" />
              <div>
                { duration }
              </div>
            </div>
          ) }

        </div>

      </div>
    </>
  )
}

export default JobSummary
